import { FC, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

import { Form, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Buttons, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Paragraph, Subheading } from "capitalrx-components-2.0/lib/typography";

import { cognitoConfirmRegistrationCode, cognitoResentSignUpCode } from "@/App/api/login";
import { ThemedButton, ThemedSubmitButton } from "@/App/components";
import { ClientAwareLink } from "@/App/components/ClientAwareLink";
import { useNavigateClient } from "@/App/hooks";

interface ConfirmRegistrationForm {
  readonly confirmationCode: string;
}

export const ConfirmEmailPage: FC = () => {
  const { t: translate } = useTranslation();
  const form = useForm<ConfirmRegistrationForm>({
    defaultValues: {
      confirmationCode: "",
    },
  });

  const alert = useAlert();

  const [searchParams] = useSearchParams();
  const navigate = useNavigateClient();
  const email = useMemo(() => {
    const fromUrl = searchParams.get("email");
    if (fromUrl) {
      return decodeURIComponent(fromUrl);
    }
    return undefined;
  }, [searchParams]);

  useEffect(() => {
    if (email === undefined) {
      navigate("/login");
    }
  }, [email]);

  const submitForm = async (formData: ConfirmRegistrationForm) => {
    try {
      await cognitoConfirmRegistrationCode(email, formData.confirmationCode);
      alert({
        message: `${translate("CONFIRM_EMAIL_PAGE.cognitoConfirmRegistrationCodeMessageSuccess")}`,
        type: "success",
        style: "toast",
      });
      navigate("/");
    } catch (e) {
      alert({
        message: e?.message || `${translate("CONFIRM_EMAIL_PAGE.cognitoConfirmRegistrationCodeMessageError")}`,
        type: "error",
        style: "toast",
      });
    }
  };

  const reRequestCode = async () => {
    try {
      await cognitoResentSignUpCode(email);
    } catch (e) {
      alert({
        message: e?.data?.message || `${translate("CONFIRM_EMAIL_PAGE.cognitoResentSignUpCodeMessageError")}`,
        type: "error",
        style: "toast",
      });
    }
  };

  return (
    <Form form={form} onSubmit={submitForm} testId="confirm-email">
      <Grid>
        <Grid.Span columns={12}>
          <Stack>
            <Subheading textAlign="center">{translate("CONFIRM_EMAIL_PAGE.confirmEmail")}</Subheading>
            <Paragraph textAlign="center">{translate("CONFIRM_EMAIL_PAGE.enterConfirmationCode")}</Paragraph>
          </Stack>
        </Grid.Span>

        <Grid.Span columns={12}>
          <Textbox.Field
            testId="confirmation-code"
            name="confirmationCode"
            label={translate("CONFIRM_EMAIL_PAGE.confirmationCode")}
            placeholder={translate("CONFIRM_EMAIL_PAGE.confirmationCode")}
            required
          />
        </Grid.Span>

        <Grid.Span columns={12}>
          <ThemedSubmitButton testId="confirm">{translate("CONFIRM_EMAIL_PAGE.confirm")}</ThemedSubmitButton>
        </Grid.Span>

        <Grid.Span columns={12}>
          <Shelf>
            <Shelf.Spacer />
            <Paragraph>{translate("CONFIRM_EMAIL_PAGE.didntReceiveEmail")}</Paragraph>
            <ThemedButton testId="re-request-code" appearance={Buttons.Functional.Tertiary.Compact} onClick={reRequestCode}>
              {translate("CONFIRM_EMAIL_PAGE.resendCode")}
            </ThemedButton>
            <Shelf.Spacer />
          </Shelf>
        </Grid.Span>

        <Shelf>
          <Shelf.Spacer />
          <ClientAwareLink testId="back-to-sign-in" to="/login">
            {translate("CONFIRM_EMAIL_PAGE.alreadyHaveAccount")}
          </ClientAwareLink>
        </Shelf>
      </Grid>
    </Form>
  );
};
